<script setup>
import {computed, nextTick, onMounted, ref, watch} from "vue";
import ManualLocationInput from "@/components/ai-onboarding/partials/ManualLocationInput.vue";
import CustomMultiselect from "@/components/global-themed/CustomMultiselect.vue";
import axios from "axios";
import {fetchSuggestions} from "@/composables/ai-onboarding/useLocationSuggestions";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import {Steps} from "@/composables/ai-onboarding/useSteps";
import {VueDraggable} from "vue-draggable-plus";
import AllServicesAddedEmptyState from "@/components/ai-onboarding/partials/AllServicesAddedEmptyState.vue";
import ProductAndServiceBox from "@/components/ai-onboarding/partials/ProductAndServiceBox.vue";
import AddServiceEmptyState from "@/components/ai-onboarding/partials/AddServiceEmptyState.vue";

const emit = defineEmits(['next', 'update:modelValue']);

const limit = 5;

const city = ref('');
const showManualInput = ref(false);
const distance = ref(5);
const search = ref('');
const suggestionsContainer = ref(null);
const locationSuggestions = ref([]);

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => ([]),
  },
});

const selectedLocations = ref([...props.modelValue]);

const totalLocationLength = computed(() => locationSuggestions.value.length + selectedLocations.value.length);

onMounted(async () => {
  const {data} = await axios.get('/ajax/v2/ai-onboarding/locations/selected');

  selectedLocations.value = data.data?.map((location, index) => {
    return {
      id: index + 1,
      city: location,
      manual: true,
    };
  }) ?? [];
});

const distances = {
  1: '1 mile',
  2: '2 miles',
  5: '5 miles',
  10: '10 miles',
};

const {data: locationSuggestionData, isLoading} = fetchSuggestions(city, distance);

watch(locationSuggestionData, function () {
  if (!locationSuggestionData.value) {
    return;
  }

  // removing already selected cities from suggestions
  const existingCities = new Set(selectedLocations.value.map(loc => loc.city));
  const filteredSuggestions = locationSuggestionData.value.filter(location => !existingCities.has(location.city));

  let locationLength = totalLocationLength.value;
  const newSuggestions = filteredSuggestions.map(location => {
    locationLength += 1;

    return {
      ...location,
      id: locationLength,
      manual: false,
    };
  });

  const manuallyAddedLocations = locationSuggestions.value.filter(loc => loc.manual);

  locationSuggestions.value = [
    ...manuallyAddedLocations,
    ...newSuggestions,
  ];
});

watch(selectedLocations, () => emit('update:modelValue', selectedLocations.value));

const fetchCities = async (query) => {
  if (query.length < 3) {
    return;
  }

  const {data} = await axios.get(`/ajax/v2/ai-onboarding/cities?searchString=${query}`);

  return data.data.map(city => ({
    value: city.id,
    label: city.city + (city.admin_name ? `, ${city.admin_name}` : ''),
  }));
}

const addManualLocation = (location) => {
  if (selectedLocations.value.some(loc => loc.city === location)) {
    return;
  }

  // remove the same location suggestion if one exists
  locationSuggestions.value = locationSuggestions.value.filter(loc => loc.city !== location);

  const newLocation = {
    id: totalLocationLength.value + 1,
    city: location,
    manual: true,
  };

  if (selectedLocations.value.length >= limit) {
    locationSuggestions.value.unshift(newLocation);

    emit('update:modelValue', selectedLocations.value);
    return;
  }

  selectedLocations.value.push(newLocation);

  emit('update:modelValue', selectedLocations.value);
}

const handleContinue = async () => {
  try {
    await axios.post('/ajax/v2/ai-onboarding/locations', {
      locations: selectedLocations.value.map(location => location.city) ?? [],
    });

    emit('next', Steps.BUSINESS_DESCRIPTION);
  } catch (error) {
    throw new Error('Something went wrong');
  }
}

watch([city, distance, locationSuggestions], async ([newCity, newDistance, newSuggestions]) => {
  if (newCity && newDistance && newSuggestions.length > 0) {
    await nextTick();

    if (suggestionsContainer.value) {
      suggestionsContainer.value.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }
});

defineExpose({
  handleContinue
});
</script>

<template>
  <div
    ref="suggestionsContainer"
    class="tw-flex tw-gap-y-8 tw-flex-col tw-items-center tw-w-full"
  >
    <div
      class="tw-flex tw-flex-col tw-gap-20 tw-w-[550px]"
    >
      <div
        class="tw-flex tw-flex-col tw-w-full"
        :class="[
          showManualInput ? 'tw-gap-12' : 'tw-gap-4'
        ]"
      >
        <div
          class="tw-flex tw-gap-8"
        >
          <div class="tw-flex tw-flex-col tw-w-full tw-gap-4">
            <label
              for="city"
              class="tw-self-start tw-text-lg tw-text-grey-dark tw-font-bold"
            >
              Enter location:
            </label>
            <CustomMultiselect
              id="city"
              v-model="city"
              class="tw-text-left tw-w-[358px] tw-h-13 tw-relative"
              :close-on-select="true"
              :options="async (query) => await fetchCities(query)"
              :searchable="true"
              placeholder="Search for Towns or Cities"
              :caret="false"
              :no-options-text="!search.length ? 'Type to search for a town or city' : 'No results - Add manually below'"
              :min-chars="3"
              :filter-results="false"
              :resolve-on-load="false"
              :delay="0"
              :can-clear="true"
              @search-change="(query) => search = query"
            >
              <template #icon>
                <svg-vue
                  icon="icons/location-pin"
                  class="tw-w-6 tw-h-7 tw-text-grey-dark"
                />
              </template>
            </CustomMultiselect>
          </div>

          <div class="tw-flex tw-flex-col tw-w-full tw-gap-4">
            <label
              for="city"
              class="tw-self-start tw-text-lg tw-text-grey-dark tw-font-bold"
            >
              Search radius:
            </label>
            <CustomMultiselect
              id="distance"
              v-model="distance"
              class="tw-text-left tw-w-40 tw-h-13"
              :close-on-select="true"
              :options="distances"
              :searchable="false"
              :can-deselect="false"
            />
          </div>
        </div>

        <span
          v-if="!showManualInput"
          class="tw-text-base tw-text-blue tw-underline tw-font-bold tw-self-start tw-cursor-pointer"
          @click="showManualInput = true"
        >
          Can’t find your location? Add it manually.
        </span>

        <ManualLocationInput
          v-if="showManualInput"
          @close="showManualInput = false"
          @location-added="addManualLocation"
        />
      </div>
    </div>

    <div class="tw-flex tw-justify-between tw-w-full">
      <div class="tw-flex tw-flex-col tw-w-[432px] tw-text-left tw-relative">
        <LoadingBlock v-if="isLoading" />

        <div
          v-else
          class="tw-h-full"
        >
          <VueDraggable
            v-model="locationSuggestions"
            class="tw-p-4 tw-pt-5 tw-border-2 tw-border-grey-mid tw-rounded-4xl tw-flex tw-flex-col tw-gap-3 tw-min-h-[140px] tw-h-full"
            group="services"
            animation="150"
            filter=".disabled-location"
            :sort="false"
          >
            <ProductAndServiceBox
              v-for="location in locationSuggestions"
              :key="location.id"
              :class="{
                'disabled-location tw-cursor-not-allowed' : selectedLocations.length >= limit
              }"
              :text="location.city"
            />
          </VueDraggable>
        </div>

        <AllServicesAddedEmptyState
          v-if="!locationSuggestions.length && !isLoading"
          class="tw-absolute tw-w-[396px] tw-top-[22px] tw-left-[18px]"
        >
          Search for a city above
        </AllServicesAddedEmptyState>
      </div>

      <div class="tw-flex tw-flex-col tw-justify-between tw-gap-y-4 tw-w-[432px] tw-relative">
        <div
          class="tw-text-grey tw-flex tw-gap-2 tw-items-center tw-absolute tw-top-[16px] tw-w-full tw-justify-center tw-p-2"
        >
          <span class="tw-text-grey tw-text-base">Drag & drop up to {{ limit }} locations by priority here.</span>
        </div>

        <VueDraggable
          v-model="selectedLocations"
          class="tw-p-4 tw-pt-16 tw-border-2 tw-border-grey-mid tw-rounded-4xl tw-flex tw-flex-col tw-gap-3 tw-min-h-[192px] tw-h-full"
          group="services"
          animation="150"
        >
          <ProductAndServiceBox
            v-for="(location, index) in selectedLocations"
            :key="location.id"
            :text="location.city"
            :index="index + 1"
          />
        </VueDraggable>

        <AddServiceEmptyState
          v-if="!selectedLocations.length"
          class="tw-absolute tw-w-[396px] tw-top-[70px] tw-left-[18px]"
        >
          Add location
        </AddServiceEmptyState>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sortable-chosen {
  border: 2px solid #0094FF;
}
</style>
